import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { Phone, Copy, Check, Mail, MessageSquare, ExternalLink } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n || 0) + " €";

export default function PhoneBookingDialog({ open, onOpenChange, onCreated }) {
  const [accs, setAccs] = useState([]);
  const [form, setForm] = useState({
    accommodation_id: "", arrivee: "", depart: "", adultes: 2, enfants: 0,
    nom: "", email: "", telephone: "", message: "",
    late_checkout: false, pet: false, transfer: false, cancellation_insurance: false,
    payment_mode: "deposit",
  });
  const [quote, setQuote] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/accommodations").then((r) => setAccs(r.data)).catch(() => {});
      setResult(null);
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const active = accs.find((a) => a.id === form.accommodation_id) || null;

  const fetchQuote = useCallback(async () => {
    if (!form.accommodation_id || !form.arrivee || !form.depart || form.arrivee >= form.depart) { setQuote(null); return; }
    try {
      const { data } = await api.post("/bookings/quote", {
        accommodation_id: form.accommodation_id, arrivee: form.arrivee, depart: form.depart,
        adultes: Number(form.adultes), enfants: Number(form.enfants),
        late_checkout: form.late_checkout, pet: form.pet, transfer: form.transfer,
        cancellation_insurance: form.cancellation_insurance,
      });
      setQuote(data);
    } catch { setQuote(null); }
  }, [form.accommodation_id, form.arrivee, form.depart, form.adultes, form.enfants, form.late_checkout, form.pet, form.transfer, form.cancellation_insurance]);

  useEffect(() => { if (open) fetchQuote(); }, [open, fetchQuote]);

  const amountToPay = !quote ? 0 : form.payment_mode === "full" ? quote.total : quote.deposit;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accommodation_id) { toast.error("Choisissez un hébergement"); return; }
    if (!quote?.available) { toast.error("Dates indisponibles"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/bookings/phone", {
        ...form,
        voyageurs: Number(form.adultes) + Number(form.enfants),
        adultes: Number(form.adultes), enfants: Number(form.enfants),
        origin_url: window.location.origin,
      });
      setResult(data);
      if (data.payment_mode === "none") {
        toast.success("Réservation enregistrée (dates bloquées)");
        onCreated && onCreated();
        onOpenChange(false);
      } else {
        toast.success("Réservation créée · lien de paiement généré");
        onCreated && onCreated();
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(result.url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { console.error("Clipboard copy failed:", e); }
  };

  const label = "text-xs font-medium text-ink-600 mb-1 block";
  const input = "w-full rounded-xl border border-ink-400/25 bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  const pm = form.payment_mode;

  const mailBody = result ? encodeURIComponent(`Bonjour ${form.nom},\n\nVoici le lien pour régler votre réservation au Domaine de La Bégaudière :\n${result.url}\n\nÀ très bientôt,\nLe Domaine de La Bégaudière`) : "";
  const smsBody = result ? encodeURIComponent(`Domaine de La Bégaudière : lien de paiement de votre réservation : ${result.url}`) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="phone-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-ink-900 flex items-center gap-2">
            <Phone size={20} className="text-brand" /> Nouvelle réservation (téléphone)
          </DialogTitle>
        </DialogHeader>

        {result && result.url ? (
          <div className="space-y-4" data-testid="phone-booking-result">
            <div className="rounded-xl bg-brand-50/60 border border-brand/20 p-4">
              <p className="text-sm text-ink-700">Réservation créée pour <b>{form.nom || "—"}</b>. Montant à régler ({result.payment_mode === "full" ? "total" : "acompte 30 %"}) : <b>{eur(result.amount)}</b>.</p>
              <p className="text-xs text-ink-500 mt-1">Envoyez ce lien de paiement sécurisé au client. Les dates sont déjà bloquées.</p>
            </div>
            <div>
              <label className={label}>Lien de paiement</label>
              <div className="flex gap-2">
                <input readOnly value={result.url} className={`${input} text-xs`} data-testid="phone-booking-link" />
                <button onClick={copyLink} className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-brand text-white px-3 text-sm font-medium hover:bg-brand-600" data-testid="phone-booking-copy">
                  {copied ? <Check size={16} /> : <Copy size={16} />}{copied ? "Copié" : "Copier"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <a href={`mailto:${form.email}?subject=${encodeURIComponent("Votre réservation · La Bégaudière")}&body=${mailBody}`} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-ink-400/30 py-2.5 text-sm text-ink-700 hover:bg-black/5" data-testid="phone-booking-email"><Mail size={15} /> E-mail</a>
              <a href={`sms:${form.telephone}?body=${smsBody}`} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-ink-400/30 py-2.5 text-sm text-ink-700 hover:bg-black/5" data-testid="phone-booking-sms"><MessageSquare size={15} /> SMS</a>
              <a href={result.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-ink-400/30 py-2.5 text-sm text-ink-700 hover:bg-black/5" data-testid="phone-booking-open"><ExternalLink size={15} /> Ouvrir</a>
            </div>
            <button onClick={() => onOpenChange(false)} className="w-full rounded-full bg-ink-900 text-white py-3 font-medium hover:opacity-90" data-testid="phone-booking-done">Terminé</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className={label}>Hébergement *</label>
              <select value={form.accommodation_id} onChange={set("accommodation_id")} required className={input} data-testid="phone-acc-select">
                <option value="">—</option>
                {accs.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type === "gite" ? "Gîte" : "Chambre"})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Arrivée *</label><input type="date" value={form.arrivee} onChange={set("arrivee")} required className={input} data-testid="phone-arrivee" /></div>
              <div><label className={label}>Départ *</label><input type="date" value={form.depart} onChange={set("depart")} required className={input} data-testid="phone-depart" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Adultes</label>
                <select value={form.adultes} onChange={set("adultes")} className={input} data-testid="phone-adultes">{[1,2,3,4,5,6,7,8].map((n)=><option key={n} value={n}>{n}</option>)}</select></div>
              <div><label className={label}>Enfants</label>
                <select value={form.enfants} onChange={set("enfants")} className={input} data-testid="phone-enfants">{[0,1,2,3,4,5,6].map((n)=><option key={n} value={n}>{n}</option>)}</select></div>
            </div>
            <div><label className={label}>Nom du client *</label><input value={form.nom} onChange={set("nom")} required className={input} data-testid="phone-nom" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>E-mail</label><input type="email" value={form.email} onChange={set("email")} className={input} data-testid="phone-email" /></div>
              <div><label className={label}>Téléphone</label><input value={form.telephone} onChange={set("telephone")} className={input} data-testid="phone-tel" /></div>
            </div>

            {quote && quote.available && (
              <div className="rounded-xl border border-brand/20 bg-brand-50/40 p-3.5 text-sm space-y-1" data-testid="phone-quote">
                <div className="flex justify-between text-ink-600"><span>Total du séjour · {quote.nights} nuit(s)</span><span className="font-semibold text-ink-900">{eur(quote.total)}</span></div>
                <div className="flex justify-between text-ink-500 text-xs"><span>Acompte 30 %</span><span>{eur(quote.deposit)}</span></div>
                <div className="flex justify-between text-ink-500 text-xs"><span>Caution (sur place)</span><span>{eur(quote.caution)}</span></div>
                <p className="text-[11px] text-ink-400 pt-1">Solde et caution réglés sur place : espèces ou CB uniquement.</p>
              </div>
            )}
            {quote && !quote.available && <p className="text-sm text-red-600" data-testid="phone-unavailable">Ces dates ne sont pas disponibles.</p>}

            <div>
              <label className={label}>Paiement</label>
              <div className="grid grid-cols-3 gap-2" data-testid="phone-payment-mode">
                {[["deposit","Acompte 30 %"],["full","Total 100 %"],["none","Aucun (bloquer)"]].map(([v,l]) => (
                  <button type="button" key={v} onClick={() => setForm((f)=>({...f, payment_mode: v}))}
                    className={`rounded-xl border px-2 py-2.5 text-xs font-medium transition-colors ${pm===v ? "border-brand bg-brand text-white" : "border-ink-400/25 bg-white text-ink-600 hover:border-brand"}`}
                    data-testid={`phone-pm-${v}`}>{l}</button>
                ))}
              </div>
              {pm !== "none" && quote?.available && <p className="text-xs text-ink-500 mt-1.5">Un lien de paiement de <b>{eur(amountToPay)}</b> sera généré à envoyer au client.</p>}
              {pm === "none" && <p className="text-xs text-ink-500 mt-1.5">Aucun paiement en ligne : les dates seront simplement bloquées.</p>}
            </div>

            <button type="submit" disabled={sending || !quote?.available} className="w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 transition-colors disabled:opacity-50" data-testid="phone-submit">
              {sending ? "Création…" : pm === "none" ? "Créer et bloquer les dates" : "Créer et générer le lien de paiement"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
